// apps/frontend/src/components/common/MarketingConsentBanner.tsx
// Banner RGPD discret qui propose aux users existants (inscrits avant le
// fix du 2026-05-16) de donner explicitement leur consentement marketing.
// Cible : les ~87 premiers users dont le consentement n'a jamais ete
// stocke en BDD a cause d'un hardcode bug. Affiche uniquement quand :
// - user authentifie
// - acceptedMarketingAt est null
// - createdAt < date du fix (on ne reproposo pas aux nouveaux inscrits
//   qui ont activement choisi de ne pas cocher la case)
// - pas snooze recemment (snooze 30 jours apres dismiss)

import React, { useMemo, useState } from 'react';
import { Mail, X, Check, Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import apiClient from '../../services/apiClient';

// Date du fix qui a branche le consentement marketing bout-en-bout.
// Tous les users inscrits avant cette date ont vu leur opt-in perdu en
// silence — on leur demande maintenant explicitement.
const MARKETING_CONSENT_FIX_DATE = new Date('2026-05-16T00:00:00Z');
const SNOOZE_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 jours
const SNOOZE_KEY = 'glowp_marketing_consent_snoozed_until';

const MarketingConsentBanner: React.FC = () => {
  const { user, getProfile } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [success, setSuccess] = useState(false);

  const shouldShow = useMemo(() => {
    if (!user || dismissed) return false;
    // Deja opted-in (ou opted-out explicitement avec un timestamp) : on
    // ne demande plus. Si `acceptedMarketingAt` est null, c'est qu'on n'a
    // jamais collecte de reponse pour ce user.
    if (user.acceptedMarketingAt) return false;
    if (!user.createdAt) return false;
    // On ne sollicite que les users inscrits avant le fix — ceux d'apres
    // ont eu une vraie case a cocher.
    const createdAt = new Date(user.createdAt);
    if (createdAt >= MARKETING_CONSENT_FIX_DATE) return false;
    // Snooze : si le user a clique "Plus tard" recemment, on respecte.
    try {
      const snoozedRaw = localStorage.getItem(SNOOZE_KEY);
      if (snoozedRaw) {
        const snoozedUntil = parseInt(snoozedRaw, 10);
        if (Number.isFinite(snoozedUntil) && snoozedUntil > Date.now()) {
          return false;
        }
      }
    } catch {
      // localStorage indisponible (Safari private) : on affiche quand meme,
      // le user pourra re-dismiss (la session se termine de toute facon).
    }
    return true;
  }, [user, dismissed]);

  if (!shouldShow) return null;

  const handleOptIn = async () => {
    setSubmitting(true);
    try {
      const res = await apiClient.patchMyProfile({ acceptMarketing: true });
      if (res.success) {
        setSuccess(true);
        // Refresh le user pour que acceptedMarketingAt soit a jour et que
        // le banner disparaisse au prochain render (apres l'animation).
        await getProfile();
        // Petit delai pour afficher le check avant que le banner disparaisse.
        setTimeout(() => setDismissed(true), 1200);
      } else {
        // Erreur backend : on snooze 24h pour ne pas spammer le user, mais
        // on n'affiche pas d'erreur visible (cas marginal, non bloquant).
        try {
          localStorage.setItem(
            SNOOZE_KEY,
            String(Date.now() + 24 * 60 * 60 * 1000)
          );
        } catch {
          /* ignore */
        }
        setDismissed(true);
      }
    } catch {
      try {
        localStorage.setItem(
          SNOOZE_KEY,
          String(Date.now() + 24 * 60 * 60 * 1000)
        );
      } catch {
        /* ignore */
      }
      setDismissed(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnooze = () => {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DURATION_MS));
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div
      className="fixed z-40 left-4 right-4 md:left-auto md:right-6 md:max-w-sm rounded-2xl shadow-2xl border bg-white p-4 sm:p-5 animate-in fade-in slide-in-from-bottom-4 duration-300"
      style={{
        borderColor: 'var(--brand-border)',
        // Au-dessus de la barre de nav mobile + barre home iPhone.
        bottom: 'calc(env(safe-area-inset-bottom) + 5rem)',
      }}
      role="dialog"
      aria-label="Consentement marketing Glowp"
    >
      <button
        type="button"
        onClick={handleSnooze}
        className="absolute top-2 right-2 p-1.5 rounded-lg hover:bg-brand-sand transition-colors text-brand-muted hover:text-brand-warm"
        aria-label="Plus tard"
        disabled={submitting}
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'var(--brand-light)' }}
        >
          {success ? (
            <Check className="w-4 h-4 text-brand-500" />
          ) : (
            <Mail className="w-4 h-4 text-brand-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          {success ? (
            <>
              <p className="text-sm font-semibold text-brand-dark">
                Merci, c&apos;est noté
              </p>
              <p className="text-xs text-brand-warm mt-0.5">
                Tu recevras nos actualités et bons plans.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-brand-dark leading-snug">
                Reste au courant des nouveautés Glowp
              </p>
              <p className="text-xs text-brand-warm mt-1 leading-relaxed">
                Bons plans, nouveaux articles, conseils — par email, jamais plus
                d&apos;une fois par semaine. Désabonnement en 1 clic.
              </p>
            </>
          )}
        </div>
      </div>

      {!success && (
        <div className="flex gap-2 mt-3 pl-12">
          <button
            type="button"
            onClick={handleSnooze}
            disabled={submitting}
            className="flex-1 py-2 rounded-xl text-xs font-semibold transition-all hover:bg-brand-sand text-brand-warm disabled:opacity-50"
          >
            Plus tard
          </button>
          <button
            type="button"
            onClick={handleOptIn}
            disabled={submitting}
            className="flex-1 py-2 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            style={{ backgroundColor: 'var(--brand-500)' }}
          >
            {submitting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              "Oui, m'inscrire"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default MarketingConsentBanner;
